import { useEffect, useRef } from "react";

// Cloudflare Turnstile widget (signup bot protection).
//
// Rendered only when VITE_TURNSTILE_SITE_KEY is set at build time; the API
// enforces verification only when its matching secret is set, so local dev
// works with neither configured. Tokens are single-use: bump `resetNonce` to
// force a fresh challenge after a failed submit.

export const TURNSTILE_SITE_KEY: string =
  (import.meta.env["VITE_TURNSTILE_SITE_KEY"] as string | undefined) ?? "";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      theme?: "light" | "dark" | "auto";
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Failed to load Turnstile"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export function TurnstileWidget({
  onToken,
  resetNonce = 0,
}: {
  onToken: (token: string | null) => void;
  resetNonce?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    let widgetId: string | null = null;
    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => onToken(null));

    return () => {
      cancelled = true;
      if (widgetId !== null) window.turnstile?.remove(widgetId);
      onToken(null);
    };
    // onToken is deliberately excluded: re-rendering the challenge on every
    // parent render would reset it while the user is mid-interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetNonce]);

  if (!TURNSTILE_SITE_KEY) return null;

  return <div ref={containerRef} style={{ minHeight: 65 }} />;
}
